@if (!isAuthenticated) {
<app-portal-navbar></app-portal-navbar>
}

<ion-content [fullscreen]="!isAuthenticated" [scrollEvents]="true">

  <!-- HERO -->
  <div class="hero" [class.hero--public]="!isAuthenticated" [class.hero--shell]="isAuthenticated">
    <div class="hero-grid-overlay"></div>
    <p class="eyebrow">{{ 'status.heroEyebrow' | translate }}</p>
    <h1>{{ 'status.heroTitleLine1' | translate }}<br><span class="gradient-text">{{ 'status.heroTitleLine2' | translate }}</span></h1>
    <p class="hero-sub">{{ 'status.heroSub' | translate }}</p>
  </div>

  <!-- Rich tooltip for a single history-bar cell: date, exact state and uptime, or a no-data note. -->
  <ng-template #dayTip let-day>
    @let tip = historyCellTip(day);
    @if (tip.hasData) {
    <div class="day-tip">
      <div class="day-tip__date">{{ tip.dateLabel }}</div>
      <div class="day-tip__state" [class]="'state-' + stateClass(day!.worstState)">
        <span class="day-tip__dot"></span>{{ tip.stateLabelKey | translate }}
      </div>
      <div class="day-tip__uptime">{{ tip.uptimeLabel }}% {{ 'status.uptime' | translate }}</div>
    </div>
    } @else {
    <div class="day-tip day-tip--empty">
      <ion-icon name="help-circle-outline"></ion-icon>
      <span>{{ 'status.noData' | translate }}</span>
    </div>
    }
  </ng-template>

  <div class="status-wrapper">

    @if (isLoading) {
    <div class="skeleton" aria-busy="true" [attr.aria-label]="'status.loading' | translate">
      <div class="skeleton-banner shimmer"></div>
      @for (i of [1, 2, 3, 4]; track i) {
      <div class="skeleton-card">
        <div class="skeleton-card-header">
          <div class="skeleton-avatar shimmer"></div>
          <div class="skeleton-title shimmer"></div>
        </div>
        @for (j of [1, 2, 3]; track j) {
        <div class="skeleton-row">
          <div class="skeleton-label shimmer"></div>
          <div class="skeleton-bar shimmer"></div>
        </div>
        }
      </div>
      }
    </div>
    } @else if (loadError) {
    <div class="error-box">
      <ion-icon name="cloud-offline-outline"></ion-icon>
      <p>{{ 'status.loadError' | translate }}</p>
      <ion-button shape="round" size="small" (click)="refresh()">{{ 'status.retry' | translate }}</ion-button>
    </div>
    } @else {

    <!-- OVERALL BANNER -->
    <div class="overall-banner" [class]="'state-' + stateClass(overallState)">
      <div class="overall-icon">
        <ion-icon name="pulse-outline"></ion-icon>
      </div>
      <div class="overall-text">
        <span class="overall-label">{{ stateLabelKey(overallState) | translate }}</span>
        @if (generatedAtUtc) {
        <span class="overall-timestamp">{{ 'status.generatedAt' | translate }}: {{ formatTimestamp(generatedAtUtc) }}</span>
        }
        <span class="overall-hint">{{ 'status.refreshHint' | translate }}</span>
      </div>
      <ion-button fill="clear" size="small" (click)="refresh()" [attr.aria-label]="'status.refresh' | translate">
        <ion-icon name="refresh-outline" slot="icon-only"></ion-icon>
      </ion-button>
    </div>

    <!-- The history is loaded on its own; if it fails the cards above are still right, only the bars stay empty. -->
    @if (historyError) {
    <div class="notice" role="status">
      <ion-icon name="information-circle-outline" aria-hidden="true"></ion-icon>
      <span>{{ 'status.historyUnavailable' | translate }}</span>
    </div>
    }

    <!-- COMPONENTS -->
    <div class="components-list">
      @for (c of components; track c.key) {
      <div class="component-card" [class]="'state-' + stateClass(c.state)">
        <button type="button" class="component-header" (click)="toggleExpanded(c)" [attr.aria-expanded]="c.expanded">
          <div class="component-icon">
            @if (c.iconSrc) {
            <img [src]="c.iconSrc" alt="" class="component-icon__mark" />
            } @else {
            <ion-icon [name]="c.icon"></ion-icon>
            }
          </div>
          <div class="component-meta">
            <span class="component-name">{{ c.labelKey | translate }}</span>
            <span class="component-state">{{ stateLabelKey(c.state) | translate }}{{ c.stale ? (' · ' + ('status.stale' | translate)) : '' }}</span>
          </div>
          <ion-icon class="component-status-icon" [name]="stateIcon(c.state)"></ion-icon>
          <ion-icon class="component-chevron" [class.component-chevron--open]="c.expanded" name="chevron-down-outline"
                    [attr.aria-label]="(c.expanded ? 'status.collapse' : 'status.expand') | translate"></ion-icon>
        </button>

        <!-- Active incidents that name this product, next to the colour they cause. The full thread stays in the list below. -->
        @for (incident of c.incidents; track incident.id) {
        @let latest = latestUpdate(incident);
        <div class="card-incident" [class]="'state-' + stateClass(incident.impact)">
          <div class="card-incident__head">
            <ion-icon name="alert-circle-outline" aria-hidden="true"></ion-icon>
            <span class="card-incident__title">{{ incident.title }}</span>
            <span class="incident-impact">{{ impactLabelKey(incident.impact) | translate }}</span>
          </div>
          @if (latest) {
          <div class="card-incident__update">
            <span class="update-stage">{{ stageLabelKey(latest.stage) | translate }}</span>
            <span class="update-message">{{ latest.message }}</span>
            <span class="update-time">{{ formatTimestamp(latest.postedAtUtc) }}</span>
          </div>
          }
        </div>
        }

        <div class="uptime-bar uptime-bar--group" [class.uptime-bar--loading]="historyLoading">
          @for (day of c.visibleDays; track $index) {
          <span class="uptime-cell" [class]="day ? ('state-' + stateClass(day.worstState)) : 'state-nodata'"
                [appTooltip]="dayTip" [tooltipContext]="day" tooltipPosition="top" tooltipTheme="card"></span>
          }
        </div>
        <div class="uptime-footer">
          <!-- A component whose monitoring started inside the window would otherwise sit under a
               "vor 90 Tagen" label with 88 blank cells, which reads as broken rather than as
               "not measured yet". Name the start date instead. -->
          @if (c.monitoredSince) {
          <span class="uptime-footer-since">{{ 'status.monitoredSince' | translate:{ date: formatDate(c.monitoredSince) } }}</span>
          } @else {
          <span>{{ 'status.daysAgo' | translate:{ days: visibleDayCount } }}</span>
          }
          <span class="uptime-footer-total">
            @if (c.windowUptimePercent !== null) { {{ c.windowUptimePercent | number:'1.1-2' }}% {{ 'status.uptime' | translate }} } @else { &ndash; }
          </span>
          <span>{{ 'status.today' | translate }}</span>
        </div>

        @if (c.expanded) {
        <div class="service-list">
          @for (g of c.serviceGroups; track $index) {
          @if (g.role) {
          <div class="service-group-head">
            <span class="service-group-title">{{ roleLabelKey(g.role) | translate }}</span>
            <span class="service-group-hint">{{ roleHintKey(g.role) | translate }}</span>
          </div>
          }
          @for (s of g.rows; track s.key) {
          <div class="service-row" [class]="'state-' + stateClass(s.state)">
            <div class="service-label">
              <span class="service-label__line">
                <ion-icon [name]="s.icon"></ion-icon>
                <span class="service-name">{{ s.labelKey | translate }}</span>
              </span>
              <!-- Second line: a problem says so in the state colour, otherwise how fresh the result is. -->
              <span class="service-meta" [class.service-meta--alert]="s.monitored && s.state !== 'Operational' && s.state !== 'Unknown'">
                @if (!s.monitored) {
                {{ 'status.notMonitored' | translate }}
                } @else {
                @if (s.state !== 'Operational') { {{ stateLabelKey(s.state) | translate }}{{ s.stale ? (' · ' + ('status.stale' | translate)) : '' }}{{ s.checkedAgo ? ' · ' : '' }} }
                @if (s.checkedAgo) { {{ s.checkedAgo.key | translate:{ count: s.checkedAgo.count } }} }
                }
              </span>
            </div>
            @if (s.monitored) {
            <div class="uptime-bar uptime-bar--service" [class.uptime-bar--loading]="historyLoading">
              @for (day of s.visibleDays; track $index) {
              <span class="uptime-cell" [class]="day ? ('state-' + stateClass(day.worstState)) : 'state-nodata'"
                    [appTooltip]="dayTip" [tooltipContext]="day" tooltipPosition="top" tooltipTheme="card"></span>
              }
            </div>
            } @else {
            <p class="service-unmonitored">{{ 'status.notMonitoredHint' | translate }}</p>
            }
            <span class="service-uptime">
              @if (s.monitored && s.windowUptimePercent !== null) { {{ s.windowUptimePercent | number:'1.1-2' }}% } @else { &ndash; }
            </span>
          </div>
          }
          }
        </div>
        }
      </div>
      }
    </div>

    <!-- SCHEDULED MAINTENANCE -->
    @if (scheduledMaintenance.length > 0 || pastMaintenance.length > 0) {
    <div class="incidents-section">
      <h2 class="section-heading">{{ 'status.scheduledMaintenanceTitle' | translate }}</h2>
      @for (incident of scheduledMaintenance; track incident.id) {
      <ng-container *ngTemplateOutlet="maintenanceCard; context: { $implicit: incident, past: false }"></ng-container>
      } @empty {
      <div class="all-clear">
        <ion-icon name="checkmark-circle-outline"></ion-icon>
        <span>{{ 'status.noScheduledMaintenance' | translate }}</span>
      </div>
      }

      <!-- A window that has run its course stops competing with the ones still ahead, but
           stays one click away: customers check back to confirm the work is actually done. -->
      @if (pastMaintenance.length > 0) {
      <button type="button" class="history-toggle" (click)="togglePastMaintenance()"
              [attr.aria-expanded]="showPastMaintenance">
        <ion-icon [name]="showPastMaintenance ? 'chevron-up-outline' : 'chevron-down-outline'" aria-hidden="true"></ion-icon>
        {{ (showPastMaintenance ? 'status.pastMaintenanceHide' : 'status.pastMaintenanceShow')
           | translate:{ count: pastMaintenance.length } }}
      </button>
      @if (showPastMaintenance) {
      <div class="incidents-section--muted">
        @for (incident of pastMaintenance; track incident.id) {
        <ng-container *ngTemplateOutlet="maintenanceCard; context: { $implicit: incident, past: true }"></ng-container>
        }
      </div>
      }
      }
    </div>
    }

    <!-- ACTIVE INCIDENTS -->
    <div class="incidents-section">
      <h2 class="section-heading">{{ 'status.activeIncidentsTitle' | translate }}</h2>
      @if (activeIncidents.length === 0) {
      <div class="all-clear">
        <ion-icon name="checkmark-circle-outline"></ion-icon>
        <span>{{ 'status.noIncidents' | translate }}</span>
      </div>
      } @else {
      @for (incident of activeIncidents; track incident.id) {
      <div class="incident-card" [class]="'state-' + stateClass(incident.impact)">
        <div class="incident-header">
          <ion-icon name="alert-circle-outline"></ion-icon>
          <span class="incident-title">{{ incident.title }}</span>
          <span class="incident-impact">{{ impactLabelKey(incident.impact) | translate }}</span>
        </div>
        <!-- Affected products: either the admin's explicit tagging or, failing that, derived from
             the incident's ServiceKeys — see StatusFunction.ToIncidentDto. -->
        @if (incident.affectedGroups.length > 0) {
        <div class="incident-products">
          @for (g of incident.affectedGroups; track g) {
          <span class="incident-product">{{ productLabelKey(g) | translate }}</span>
          }
        </div>
        }
        <div class="incident-updates">
          @for (u of incident.updates; track u.postedAtUtc) {
          <div class="incident-update">
            <span class="update-stage">{{ stageLabelKey(u.stage) | translate }}</span>
            <span class="update-message">{{ u.message }}</span>
            <span class="update-time">{{ formatTimestamp(u.postedAtUtc) }}</span>
          </div>
          }
        </div>
      </div>
      }
      }
    </div>

    <!-- RECENTLY RESOLVED -->
    @if (recentlyResolvedIncidents.length > 0) {
    <div class="incidents-section incidents-section--muted">
      <h2 class="section-heading">{{ 'status.recentlyResolvedTitle' | translate }}</h2>
      @for (incident of recentlyResolvedIncidents; track incident.id) {
      <div class="incident-card incident-card--resolved">
        <div class="incident-header">
          <ion-icon name="checkmark-circle-outline"></ion-icon>
          <span class="incident-title">{{ incident.title }}</span>
          <span class="incident-impact">{{ impactLabelKey(incident.impact) | translate }}</span>
        </div>
        @if (incident.affectedGroups.length > 0) {
        <div class="incident-products">
          @for (g of incident.affectedGroups; track g) {
          <span class="incident-product">{{ productLabelKey(g) | translate }}</span>
          }
        </div>
        }
        <div class="incident-updates">
          @for (u of incident.updates; track u.postedAtUtc) {
          <div class="incident-update">
            <span class="update-stage">{{ stageLabelKey(u.stage) | translate }}</span>
            <span class="update-message">{{ u.message }}</span>
            <span class="update-time">{{ formatTimestamp(u.postedAtUtc) }}</span>
          </div>
          }
        </div>
      </div>
      }
    </div>
    }

    <!-- LEGEND: what the colours mean, and how far the history can be trusted. -->
    <section class="status-legend" aria-labelledby="status-legend-title">
      <h2 class="section-heading" id="status-legend-title">{{ 'status.legend.title' | translate }}</h2>
      <ul class="status-legend__list">
        @for (state of legendStates; track state) {
        <li class="status-legend__item" [class]="'state-' + stateClass(state)">
          <span class="status-legend__dot" aria-hidden="true"></span>
          <span class="status-legend__name">{{ stateLabelKey(state) | translate }}</span>
          <span class="status-legend__text">{{ legendLabelKey(state) | translate }}</span>
        </li>
        }
      </ul>
      <p class="status-legend__note">{{ 'status.legend.sampling' | translate }}</p>
    </section>

    }

    <!-- One card for both maintenance lists: a finished window is the same card, greyed out
         and filed away rather than restyled into something else. -->
    <ng-template #maintenanceCard let-incident let-past="past">
      <div class="incident-card" [class.state-maintenance]="!past" [class.incident-card--resolved]="past">
        <div class="incident-header">
          <ion-icon [name]="past ? 'checkmark-circle-outline' : 'construct-outline'"></ion-icon>
          <span class="incident-title">{{ incident.title }}</span>
          <span class="incident-impact">{{ impactLabelKey(incident.impact) | translate }}</span>
        </div>
        @if (incident.scheduledStartUtc) {
        <div class="incident-window">
          <ion-icon name="time-outline" aria-hidden="true"></ion-icon>
          <span>{{ formatTimestamp(incident.scheduledStartUtc) }}</span>
          @if (incident.scheduledEndUtc) { <span>&ndash; {{ formatTimestamp(incident.scheduledEndUtc) }}</span> }
        </div>
        }
        <!-- Which products this window takes down — explicitly named by the admin who scheduled it. -->
        @if (incident.affectedGroups.length > 0) {
        <div class="incident-products">
          @for (g of incident.affectedGroups; track g) {
          <span class="incident-product">{{ productLabelKey(g) | translate }}</span>
          }
        </div>
        }
        <div class="incident-updates">
          @for (u of incident.updates; track u.postedAtUtc) {
          <div class="incident-update">
            <span class="update-stage">{{ stageLabelKey(u.stage) | translate }}</span>
            <span class="update-message">{{ u.message }}</span>
            <span class="update-time">{{ formatTimestamp(u.postedAtUtc) }}</span>
          </div>
          }
        </div>
      </div>
    </ng-template>

  </div>

  <app-portal-footer></app-portal-footer>

</ion-content>
